import { getLocale, setLocale, t, type Locale, type TranslationKey, type TranslationParams } from '@devils-paw/i18n';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

interface I18nValue {
  locale: Locale;
  setAppLocale: (locale: Locale) => void;
  text: (key: TranslationKey, params?: TranslationParams) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setCurrentLocale] = useState<Locale>(() => {
    const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem('devils-paw:locale');
    return stored === 'en' || stored === 'pt-PT' ? stored : getLocale();
  });
  setLocale(locale);
  const value = useMemo<I18nValue>(() => ({
    locale,
    setAppLocale(nextLocale) {
      setLocale(nextLocale);
      localStorage.setItem('devils-paw:locale', nextLocale);
      document.documentElement.lang = nextLocale;
      setCurrentLocale(nextLocale);
    },
    text: t,
  }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null) throw new Error('useI18n must be used inside I18nProvider');
  return value;
}
